import type { RunStatus } from '@/hooks/use-run-status-poller';
import {
  getWorkflowRun,
  type WorkflowRunWithResults,
} from '@/services/workflow/get-workflow-run';

export type AgentChatRunStatus =
  | 'running'
  | 'paused'
  | 'completed'
  | 'failed'
  | 'canceled';

/** The ways a run can be over. */
export type AgentChatRunOutcome = Extract<
  AgentChatRunStatus,
  'completed' | 'failed' | 'canceled'
>;

/**
 * Whether a status names a run that is over -- the one place this set is
 * spelled out, so a status added to it later fails every caller that still
 * needs updating instead of silently falling through as "still going".
 * Takes a bare `string` rather than `AgentChatRunStatus` so it also accepts
 * the run-card's own `CardStatus`, which adds `'stopping'` on top.
 */
export function isTerminalRunStatus(
  status: string,
): status is AgentChatRunOutcome {
  return status === 'completed' || status === 'failed' || status === 'canceled';
}

export interface AgentChatRunSnapshot {
  status: AgentChatRunStatus;
  /** True while persisted events are being folded into their current state. */
  isHydrating?: boolean;
  workflowId?: string;
  startedAt?: number;
  completedAt?: number;
  currentEvent: string;
  progress: number;
  error?: string;
  connectionError?: string;
  /**
   * The node a run is currently paused at for review, or undefined when it
   * is not waiting on anyone. A pause is a context switch -- the chat is
   * talking to that node until it resumes.
   */
  pausedNode?: {
    nodeId: string;
    displayName?: string;
    /**
     * The output the node is waiting to have reviewed. The backend attaches
     * this to `node.input_required` specifically so a consumer can show what
     * is under review rather than just saying that something is.
     */
    reviewContent?: string;
    /** When the node last said it was waiting; it says so after each regeneration. */
    announcedAt: number;
  };
  /**
   * When the run's current pause began, set while `status` is `'paused'`.
   * Rides on the snapshot (and so on the run cache in useAgentChatRun)
   * rather than living only in a component ref, so a card that remounts
   * mid-pause still knows when the parked span actually started instead of
   * re-anchoring it to the moment of the remount.
   */
  pausedSince?: number;
  /** Total time this run has spent paused so far, across every pause/resume. */
  pausedTotalMs?: number;
}

/**
 * The backend's spellings for a run that is over, as the card names them.
 * Every other `RunStatus` value is a run that is still going.
 */
const TERMINAL_RUN_STATUSES: Partial<Record<RunStatus, AgentChatRunOutcome>> = {
  success: 'completed',
  failed: 'failed',
  stopped: 'canceled',
  cancelled: 'canceled',
};

/** Node results that will not run any further, for the progress ratio. */
const FINISHED_NODE_STATUSES = new Set(['completed', 'failed', 'skipped']);

export const RUN_FAILED_FALLBACK =
  'The workflow could not finish. Try again or open the workflow for details.';

const HAS_TIMEZONE = /(?:Z|[+-]\d{2}:?\d{2})$/;

export function timestampMs(value?: string | null): number | undefined {
  if (!value) return undefined;
  // Run events and run rows carry UTC timestamps with no offset on them.
  // Read as-is, `Date.parse` takes those for local time, which puts the
  // run's start hours into the future west of UTC and pins the elapsed
  // clock at zero.
  const parsed = Date.parse(HAS_TIMEZONE.test(value) ? value : `${value}Z`);
  return Number.isNaN(parsed) ? undefined : parsed;
}

/** Whole-percent progress, held under 100 until the run itself says it is done. */
export function progressOf(completed: number, total: number): number {
  if (!total) return 0;
  return Math.min(99, Math.round((completed / total) * 100));
}

export function terminalEventLabel(status: AgentChatRunOutcome): string {
  switch (status) {
    case 'completed':
      return 'Run complete';
    case 'canceled':
      return 'Run stopped';
    default:
      return 'Run failed';
  }
}

/**
 * The nodes the gateway puts on a run's sub-agent roster, which is what
 * `workflow.run_started` reports as `total_sub_agents`: every node in the
 * version payload that has an id, whatever its type. Counting the same way
 * keeps the stored progress and the streamed progress on the same scale.
 */
function rosterSize(nodes: unknown[]): number {
  let count = 0;
  for (const node of nodes) {
    if (!node || typeof node !== 'object') continue;
    const record = node as Record<string, unknown>;
    const data = record.data;
    const struct =
      data && typeof data === 'object'
        ? (data as Record<string, unknown>).struct
        : undefined;
    const structId =
      struct && typeof struct === 'object'
        ? (struct as Record<string, unknown>).react_flow_generated_id
        : undefined;
    if (record.id || record.react_flow_generated_id || structId) count += 1;
  }
  return count;
}

export interface StoredRunState {
  snapshot: AgentChatRunSnapshot;
  totalNodes: number;
  completedNodeIds: string[];
  /** True when the run is over: nothing left to follow. */
  terminal: boolean;
}

/**
 * The card's state as the backend has it stored. A reload paints a finished
 * run as finished and an active run at its real progress from this, instead
 * of replaying the run's event log to find out.
 */
export function storedRunState(run: WorkflowRunWithResults): StoredRunState {
  const status = (run.status || '').toLowerCase() as RunStatus;
  const outcome = TERMINAL_RUN_STATUSES[status];
  const nodes = Array.isArray(run.payload?.nodes) ? run.payload.nodes : [];
  const totalNodes = rosterSize(nodes);
  const completedNodeIds = (run.node_results ?? [])
    .filter((result) =>
      FINISHED_NODE_STATUSES.has((result.status || '').toLowerCase()),
    )
    .map((result) => result.node_instance_id ?? result.node_id);
  const startedAt = timestampMs(run.started_at ?? run.created_at);

  if (outcome) {
    return {
      terminal: true,
      totalNodes,
      completedNodeIds,
      snapshot: {
        status: outcome,
        isHydrating: false,
        workflowId: run.workflow_id,
        startedAt,
        completedAt:
          timestampMs(run.completed_at ?? run.updated_at) ?? Date.now(),
        currentEvent: terminalEventLabel(outcome),
        progress:
          outcome === 'completed'
            ? 100
            : progressOf(completedNodeIds.length, totalNodes),
        error:
          outcome === 'failed'
            ? run.error_message || RUN_FAILED_FALLBACK
            : undefined,
      },
    };
  }

  const paused = status === 'paused';
  return {
    terminal: false,
    totalNodes,
    completedNodeIds,
    snapshot: {
      status: paused ? 'paused' : 'running',
      isHydrating: false,
      workflowId: run.workflow_id,
      startedAt,
      currentEvent: paused ? 'Run paused' : 'Workflow running…',
      progress: progressOf(completedNodeIds.length, totalNodes),
      pausedSince: paused
        ? (timestampMs(run.pause_state?.paused_at) ?? Date.now())
        : undefined,
    },
  };
}

export async function loadStoredRunState(
  workflowId: string,
  runId: string,
  orgId: string,
): Promise<StoredRunState | null> {
  try {
    const response = await getWorkflowRun(workflowId, runId, orgId);
    const run = response?.data;
    if (!run || typeof run.status !== 'string') return null;
    return storedRunState(run);
  } catch {
    // getWorkflowRun already reports the failure. The replay still answers
    // the question, only slower.
    return null;
  }
}
